<template>
  <div class="after-sale-cancel">
    <div class="after-sale-cancel__content">
      <BackLink
        text="Tilbake til mine billetter"
        :route-to="backLinkRoute"
      />

      <h1 class="after-sale-cancel__title headline-medium">Kanseller reise</h1>

      <p>
        Velg delene av reisen du vil kansellere, eller kanseller hele reisen.
      </p>

      <div
        class="after-sale-cancel__options grid-layout fifty-fifty-grid fifty-fifty-grid--narrow"
      >
        <div
          v-if="hasMultipleSegments"
          class="after-sale-cancel__card"
        >
          <Checkbox
            id="cancel-all"
            v-model="cancelAll"
            additionalClass="checkbox--red checkbox--headline"
            @update:modelValue="handleCancelAllChange"
          >
            Kanseller alle reiser
          </Checkbox>
        </div>
      </div>

      <div
        class="after-sale-cancel__options grid-layout fifty-fifty-grid fifty-fifty-grid--narrow"
      >
        <AfterSalesTravel
          v-for="segment in allSegments"
          :key="segment.id"
          :segment="segment"
          :direction-label="
            segment.direction === 'outbound' ? 'Utreise' : 'Hjemreise'
          "
          :show-checkbox="true"
          :disabled="!hasAnyCancellableProducts(segment)"
          v-model:selected-passengers="selectedPassengers"
          :passengers="booking?.passengers || []"
          :has-passengers="true"
          :is-checked="selectedSegments[segment.id]"
          @segment-change="handleSegmentChange"
          @passenger-change="handlePassengerChange"
          class="after-sale-cancel__travel"
        />
      </div>
    </div>

    <NavigationBar class="after-sale-cancel__navigation">
      <router-link
        :to="backLinkRoute"
        class="button button--secondary button--medium"
      >
        Avbryt
      </router-link>
      <button
        class="button button--primary button--medium"
        @click="handleCancel"
        :disabled="!hasSelectedPassengers || isLoading"
      >
        <Spinner
          v-if="isLoading"
          class="after-sale-cancel__spinner"
        />
        <span v-else>Beregn Tilbakebetaling</span>
      </button>
    </NavigationBar>

    <CancellationSummaryModal
      :model-value="showCancellationModal"
      @update:model-value="handleModalClose"
      :total-price="totalSelectedPrice"
      :cancellation-fee="cancellationFee"
      :selected-passengers="selectedPassengers"
      :passengers="booking?.passengers || []"
      :all-segments="allSegments"
      :booking-number="bookingNumber"
      :cancel-response="booking || {}"
      @confirm="confirmCancellation"
      @close="handleModalClose"
    />

    <ConfirmationModal
      :is-open="showSuccessModal"
      @update:is-open="handleSuccessModalClose"
      heading="Kansellering fullført"
      message="Reisen er kansellert"
      confirm-text="Lukk"
      cancel-text=""
      @confirm="handleSuccessModalClose"
    />
  </div>
</template>

<script lang="ts">
  import {
    defineComponent,
    ref,
    computed,
    watch,
    onMounted,
    onBeforeUnmount,
  } from 'vue'
  import { useRoute, useRouter } from 'vue-router'
  import { useBookingStore } from '@/stores/bookingStore'
  import { useAuthStore } from '@/stores/oauthStore'
  import { useUserStore } from '@/stores/userStore'
  import { useJourneyStore } from '@/stores/journeyStore'
  import {
    cancelItems,
    confirmBooking,
    requestRefund,
  } from '@/services/journeyService'
  import { BookingTariffSegment, RequiredProduct } from '@/types/bookingTypes'
  import BackLink from '@/components/BaseComponents/BackLink.vue'
  import Checkbox from '@/components/BaseComponents/Checkbox.vue'
  import NavigationBar from '@/components/TravelPlanner/NavigationBar/NavigationBar.vue'
  import AfterSalesTravel from '@/components/MyPage/AfterSale/AfterSalesTravel.vue'
  import CancellationSummaryModal from '@/components/MyPage/AfterSale/CancellationSummaryModal.vue'
  import ConfirmationModal from '@/components/BaseComponents/Modal/ConfirmationModal.vue'
  import Spinner from '@/components/BaseComponents/Spinner/Spinner.vue'

  export default defineComponent({
    name: 'AfterSaleCancel',

    props: {
      bookingNumber: {
        type: String,
        required: true,
      },
    },

    components: {
      Checkbox,
      BackLink,
      NavigationBar,
      AfterSalesTravel,
      CancellationSummaryModal,
      ConfirmationModal,
      Spinner,
    },

    setup(props) {
      const bookingStore = useBookingStore()
      const journeyStore = useJourneyStore()
      const selectedPassengers = ref<Record<string, boolean>>({})
      const selectedSegments = ref<Record<string, boolean>>({})
      const cancelAll = ref(false)
      const router = useRouter()
      const route = useRoute()
      const userStore = useUserStore()
      const authStore = useAuthStore()
      const showCancellationModal = ref(false)
      const showSuccessModal = ref(false)
      const isLoading = ref(false)


      onBeforeUnmount(() => {
        showCancellationModal.value = false
      })

      const bookingNumber = computed(() => props.bookingNumber)

      const booking = computed(() => {
        const data = bookingStore.bookingData
        if (data) {
          const allProducts = [
            ...(data.outbound_booking_tariff_segments || []).flatMap(
              (s) => s.required_products || []
            ),
            ...(data.inbound_booking_tariff_segments || []).flatMap(
              (s) => s.required_products || []
            ),
          ]
          allProducts.forEach((product) => {
            if (!(product.uuid in selectedPassengers.value)) {
              selectedPassengers.value[product.uuid] = false
            }
          })
        }
        return data
      })

      const backLinkRoute = computed(() => ({
        name: 'ticketPage',
        params: { bookingNumber: props.bookingNumber },
      }))

      const allSegments = computed(() => {
        const outbound = booking.value?.outbound_booking_tariff_segments || []
        const inbound = booking.value?.inbound_booking_tariff_segments || []

        const processSegment = (s: any, direction: 'outbound' | 'inbound') => ({
          id: s.id || '',
          uuid: s.uuid || '',
          departure_station: {
            ...s.departure_station,
            connected_stations: s.departure_station?.connected_stations || [],
            meta_data: s.departure_station?.meta_data || [],
            timezone: s.departure_station?.timezone || 'Europe/Oslo',
            country_code: s.departure_station?.country_code || 'NOR',
            short_code: s.departure_station?.short_code || '',
            name: s.departure_station?.name || '',
            _u_i_c_station_code: s.departure_station?._u_i_c_station_code || '',
          },
          arrival_station: {
            ...s.arrival_station,
            connected_stations: s.arrival_station?.connected_stations || [],
            meta_data: s.arrival_station?.meta_data || [],
            timezone: s.arrival_station?.timezone || 'Europe/Oslo',
            country_code: s.arrival_station?.country_code || 'NOR',
            short_code: s.arrival_station?.short_code || '',
            name: s.arrival_station?.name || '',
            _u_i_c_station_code: s.arrival_station?._u_i_c_station_code || '',
          },
          travel_date: s.travel_date || '',
          sequence_number: s.sequence_number || 1,
          has_new_main_products: s.has_new_main_products || false,
          validity_service: s.validity_service || '',
          comfort_zone_code: s.comfort_zone_code || '',
          validity_start_date: s.validity_start_date || '',
          validity_end_date: s.validity_end_date || '',
          validity_intervals: s.validity_intervals || [],
          validity_weekdays: s.validity_weekdays || '',
          tariff_group_code: s.tariff_group_code || '',
          booking_journey_segments: s.booking_journey_segments || [],
          required_products: s.required_products || [],
          additional_products: s.additional_products || [],
          fees: s.fees || [],
          travel_info: s.travel_info || [],
          travel_info_extended: s.travel_info_extended || [],
          service_type: s.service_type || { name: '', code: '', modality: '' },
          client_ref: s.client_ref || '',
          is_international: s.is_international || false,
          service_identifier: s.service_identifier || '',
          direction,
        });
        
        return [
          ...outbound.map(s => processSegment(s, 'outbound')),
          ...inbound.map(s => processSegment(s, 'inbound')),
        ]
      })

      const hasMultipleSegments = computed(() => allSegments.value.length > 1)

      const hasSelectedPassengers = computed(() => {
        const hasSelectedPassenger = Object.values(
          selectedPassengers.value
        ).some((v) => v)

        const hasSelectedSegment = Object.values(selectedSegments.value).some(
          (v) => v
        )

        return hasSelectedPassenger || hasSelectedSegment
      })

      const totalSelectedPrice = computed(() => {
        if (!booking.value) return 0

        let total = 0
        const allSegments = [
          ...(booking.value.outbound_booking_tariff_segments || []),
          ...(booking.value.inbound_booking_tariff_segments || []),
        ]

        allSegments.forEach((segment) => {
          const selectedMainProducts =
            segment.required_products?.filter(
              (product) =>
                selectedPassengers.value[product.uuid] &&
                product.can_be_cancelled
            ) || []

          selectedMainProducts.forEach((product) => {
            total += product.price
          })

          if (selectedMainProducts.length > 0) {
            segment.additional_products?.forEach((product) => {
              const belongsToSelectedPassenger = selectedMainProducts.some(
                (mainProduct) =>
                  mainProduct.passenger_id === product.passenger_id
              )
              if (belongsToSelectedPassenger) {
                total += product.price
              }
            })
          }
        })

        return total
      })

      const cancellationFee = computed(() => {
        const profile = bookingStore.afterSalesProfile
        if (!profile?.cancel_ruleset?.rules?.length) {
          return 0
        }

        const rule = profile.cancel_ruleset.rules.find((rule) => rule.allowed)
        if (!rule || !rule.effect) {
          return 0
        }

        const feeValue = parseFloat(rule.effect)
        return isNaN(feeValue) ? 0 : feeValue
      })

      const handleSegmentChange = (segmentId: string, value: boolean) => {
        selectedSegments.value[segmentId] = value

        const segment = allSegments.value.find((s) => s.id === segmentId)
        if (segment?.required_products) {
          segment.required_products.forEach((product: RequiredProduct) => {
            if (
              product.can_be_cancelled &&
              product.after_sales_rules?.some(
                (rule: any) =>
                  rule.type === 'CANCEL' &&
                  rule.active &&
                  new Date(rule.valid_until) > new Date()
              )
            ) {
              selectedPassengers.value[product.uuid] = value
            }
          })
        }
      }

      const handlePassengerChange = (productId: string, value: boolean) => {
        selectedPassengers.value[productId] = value
      }

      const handleCancelAllChange = (value: boolean) => {
        allSegments.value.forEach((segment) => {
          if (
            segment.required_products?.some(
              (product: RequiredProduct) =>
                product.can_be_cancelled &&
                product.after_sales_rules?.some(
                  (rule: any) =>
                    rule.type === 'CANCEL' &&
                    rule.active &&
                    new Date(rule.valid_until) > new Date()
                )
            )
          ) {
            selectedSegments.value[segment.id] = value

            segment.required_products.forEach((product: RequiredProduct) => {
              if (
                product.can_be_cancelled &&
                product.after_sales_rules?.some(
                  (rule) =>
                    rule.type === 'CANCEL' &&
                    rule.active &&
                    new Date(rule.valid_until) > new Date()
                )
              ) {
                selectedPassengers.value[product.uuid] = value
              }
            })
          }
        })
      }

      const handleModalClose = () => {
        showCancellationModal.value = false
      }

      const handleSuccessModalClose = () => {
        showSuccessModal.value = false
        bookingStore.$reset()
        journeyStore.$reset()

        if (userStore.isLoggedIn) {
          router.push({ name: 'MyBookings' })
        } else {
          router.push({ name: 'Search' })
        }
      }

      onBeforeUnmount(() => {
        showCancellationModal.value = false
      })

      const handleCancel = async () => {
        try {
          if (route.params.bookingNumber) {
            await bookingStore.fetchAfterSalesProfile(
              route.params.bookingNumber as string
            )
          }

          showCancellationModal.value = true
        } catch (error) {
          console.error('Error in handleCancel:', error)
        }
      }

      const getSegmentItemsToCancel = (segment: BookingTariffSegment) => {
        const itemsToCancel: number[] = []

        const selectedMainProducts =
          segment.required_products?.filter((product: RequiredProduct) => {
            const canCancel =
              product.can_be_cancelled &&
              product.after_sales_rules?.some(
                (rule) =>
                  rule.type === 'CANCEL' &&
                  rule.active &&
                  new Date(rule.valid_until) > new Date()
              )
            return selectedPassengers.value[product.uuid] && canCancel
          }) || []

        selectedMainProducts.forEach((product: RequiredProduct) => {
          itemsToCancel.push(product.item_ref)
        })
        if (selectedMainProducts.length > 0) {
          segment.additional_products?.forEach((product: RequiredProduct) => {
            const belongsToSelectedPassenger = selectedMainProducts.some(
              (mainProduct) => mainProduct.passenger_id === product.passenger_id
            )
            if (belongsToSelectedPassenger) {
              itemsToCancel.push(product.item_ref)
            }
          })
        }

        return itemsToCancel
      }

      const confirmCancellation = async () => {
        if (!route.params.bookingNumber) return

        try {
          isLoading.value = true
          const itemsToCancel: number[] = []

          const hasSelectedSegments = Object.values(
            selectedSegments.value
          ).some((selected) => selected)

          allSegments.value.forEach((segment) => {
            if (!hasSelectedSegments || selectedSegments.value[segment.id]) {
              const segmentItems = getSegmentItemsToCancel(segment)
              itemsToCancel.push(...segmentItems)
            }
          })

          if (itemsToCancel.length === 0) return

          await cancelItems(route.params.bookingNumber as string, itemsToCancel)

          const response = await confirmBooking(
            route.params.bookingNumber as string,
            {
              triggerBookingCommunication: true,
            }
          )

          try {
            await requestRefund(
              route.params.bookingNumber as string,
              'psp',
              undefined,
              undefined
            )

            if (response?.data?.booking) {
              await bookingStore.setBookingData(response.data.booking)
              showCancellationModal.value = false
              showSuccessModal.value = true
              selectedPassengers.value = {}
              selectedSegments.value = {}
              cancelAll.value = false
            }
          } catch (refundError) {
            console.error('Error requesting refund:', refundError)
            throw new Error('Failed to process refund request')
          }
        } catch (error) {
          console.error('Error in cancellation process:', error)
          throw error
        } finally {
          isLoading.value = false
        }
      }

      watch(
        () => bookingStore.bookingData,
        (newBooking) => {
          if (newBooking) {
            const allProducts = [
              ...(newBooking.outbound_booking_tariff_segments || []).flatMap(
                (s) => s.required_products || []
              ),
              ...(newBooking.inbound_booking_tariff_segments || []).flatMap(
                (s) => s.required_products || []
              ),
            ]
            allProducts.forEach((product) => {
              if (!(product.uuid in selectedPassengers.value)) {
                selectedPassengers.value[product.uuid] = false
              }
            })
          }
        },
        { immediate: true }
      )

      return {
        booking,
        bookingStore,
        bookingNumber,
        hasMultipleSegments,
        selectedPassengers,
        selectedSegments,
        allSegments,
        backLinkRoute,
        totalSelectedPrice,
        cancellationFee,
        showCancellationModal,
        showSuccessModal,
        isLoading,
        hasSelectedPassengers,
        handleCancel,
        handleCancelAllChange,
        handleModalClose,
        handleSuccessModalClose,
        handleSegmentChange,
        handlePassengerChange,
        confirmCancellation,
        cancelAll,
        hasAnyCancellableProducts: (segment: any) => {
          return (
            segment.required_products?.some(
              (product: any) =>
                product.can_be_cancelled &&
                product.after_sales_rules?.some(
                  (rule: any) =>
                    rule.type === 'CANCEL' &&
                    rule.active &&
                    new Date(rule.valid_until) > new Date()
                )
            ) || false
          )
        },
      }
    },
  })
</script>

<style lang="scss" scoped>
  .after-sale-cancel {
    min-height: 100vh;
    padding-top: rem(32);
    padding-bottom: 5rem;

    &__content {
      max-width: $main-width;
      margin: 0 auto;
    }

    &__options {
      margin-top: 2rem;

      + .after-sale-cancel__options {
        margin-top: 0;
      }

      &:has(.checkbox--red:checked)
        + .after-sale-cancel__options
        .after-sales-travel {
        outline: 2px solid $clr-secondary;
      }
    }

    &__card {
      border: 1px solid #e0e0e0;
      border-radius: 8px;
      padding: rem(24) 24px;
      margin-bottom: 1rem;
      box-shadow: $box-shadow-card;
      background-color: white;

      .checkbox-container {
        gap: 8px;

        .checkbox-label {
          @include headline-small;
          font-size: rem(18);
        }
      }
    }

    &__travel {
      border: 1px solid #e0e0e0;
      border-radius: 8px;
      margin-bottom: 1rem;
      box-shadow: $box-shadow-card;
      background-color: white;

      .checkbox-container {
        gap: 8px;

        .checkbox-label {
          @include headline-small;
          font-size: rem(18);
        }
      }
    }

    .card {
      color: $clr-text;
      background: $clr-gray-60;
    }

    &__spinner {
      width: 1.5rem;
      height: 1.5rem;
      margin: 0 auto;
    }

    &__select-all {
      display: flex;
      flex-direction: column;

      &-title {
        font-weight: 600;
        font-size: 1.1rem;
      }

      &-description {
        color: $clr-text;
        font-size: 0.9rem;
        margin-top: 0.25rem;
      }
    }

    &__warning {
      margin-top: 1rem;
      color: $clr-error;
      font-size: 0.9rem;
    }
  }
</style>
